import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@rossum/ui/material';
import { confirmAnnotation } from '../../../../../redux/modules/annotation/actions';
import { queueHasWorkflowsSelector } from '../../../../../redux/modules/annotation/selectors';
import { pauseValidation } from '../../../../../redux/modules/beforeLeave/actions';
import { validate } from '../../../../../redux/modules/datapoints/actions';
import {
  datapointMessagesSelector,
  datapointPathSelector,
  initiallyValidatedSelector,
  isDeleteRecommendationInPathSelector,
  pendingValidationIdsSelector,
  pendingValidationSelector,
  unvalidatedContentSelector,
  validationInProgressSelector,
} from '../../../../../redux/modules/datapoints/selector';
import { openModal } from '../../../../../redux/modules/modal/actions';
import { annotationValidationSelector } from '../../../../../redux/modules/user/selectors';
import { Annotation } from '../../../../../types/annotation';
import { State } from '../../../../../types/state';

export const useConfirmButton = (annotation: Annotation) => {
  const intl = useIntl();

  const dispatch = useDispatch();

  const isConfirming = useSelector((state: State) => state.ui.confirming);

  const isReadOnly = useSelector((state: State) => state.ui.readOnly);

  const failedExport = annotation.status === 'failed_export';

  const validationInProgress = useSelector(validationInProgressSelector);

  const queueHasWorkflows = useSelector(queueHasWorkflowsSelector);

  const datapointMessages = useSelector(datapointMessagesSelector);

  const datapointsAreValid = !datapointMessages.error;

  const currentDatapointPath = useSelector(datapointPathSelector);

  const hasDeleteRecommendationInPath = useSelector(
    isDeleteRecommendationInPathSelector
  );

  const isOnDemandMode =
    useSelector(annotationValidationSelector) === 'on-demand';
  const unvalidatedContent = useSelector(unvalidatedContentSelector);
  const initiallyValidated = useSelector(initiallyValidatedSelector);
  const pendingValidation = useSelector(pendingValidationSelector);
  const pendingValidationIds = useSelector(pendingValidationIdsSelector);

  const showValidateButton =
    isOnDemandMode &&
    initiallyValidated &&
    unvalidatedContent &&
    !pendingValidation &&
    !isReadOnly &&
    !isConfirming &&
    !failedExport;

  const confirmButtonDisabled =
    isConfirming ||
    isReadOnly ||
    validationInProgress ||
    failedExport ||
    !datapointsAreValid;

  const confirmButtonStartIcon = useMemo(
    () =>
      isConfirming || (validationInProgress && !isReadOnly) ? (
        <CircularProgress size={16} color="inherit" />
      ) : null,
    [isConfirming, validationInProgress, isReadOnly]
  );

  const confirmButtonFocused =
    datapointsAreValid &&
    !currentDatapointPath.length &&
    !hasDeleteRecommendationInPath;

  const confirmButtonLabel = useMemo(
    () =>
      isConfirming
        ? { label: '', status: 'confirming' as const }
        : failedExport
          ? {
              label: intl.formatMessage({
                id: 'components.documentValidation.sidebar.failedExport',
              }),
              status: 'failedExport' as const,
            }
          : isReadOnly
            ? {
                label: intl.formatMessage({
                  id: 'components.documentValidation.sidebar.readOnlyDocument',
                }),
                status: 'readOnly' as const,
              }
            : validationInProgress
              ? {
                  label: intl.formatMessage({
                    id: 'components.documentValidation.sidebar.validating',
                  }),
                  status: 'validating' as const,
                }
              : {
                  label: intl.formatMessage({
                    id: queueHasWorkflows
                      ? 'components.documentValidation.sidebar.submitDocument'
                      : 'components.documentValidation.sidebar.confirmDocument',
                  }),
                  status: queueHasWorkflows
                    ? ('submit-workflow' as const)
                    : ('confirm' as const),
                },
    [
      failedExport,
      intl,
      isConfirming,
      isReadOnly,
      queueHasWorkflows,
      validationInProgress,
    ]
  );

  const handleConfirmClick = useCallback(() => {
    dispatch(
      pauseValidation({
        nextAction: confirmAnnotation(annotation.url),
        trigger: 'confirmAnnotation',
        reason: 'surveys',
      })
    );
  }, [annotation.url, dispatch]);

  const handleValidateClick = useCallback(() => {
    dispatch(
      validate(pendingValidationIds, annotation.url, ['user_update', 'updated'])
    );
  }, [annotation.url, dispatch, pendingValidationIds]);

  const handleConfirmKeyDown: React.KeyboardEventHandler<HTMLButtonElement> =
    useCallback(
      e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          dispatch(
            openModal({
              textId: 'annotationConfirm',
              onConfirm: handleConfirmClick,
            })
          );
        }
      },
      [dispatch, handleConfirmClick]
    );

  const result = useMemo(
    () =>
      showValidateButton
        ? {
            focused: false,
            label: {
              label: intl.formatMessage({
                id: 'components.documentValidation.sidebar.validate',
              }),
              status: 'validate' as const,
            },
            startIcon: null,
            onClick: handleValidateClick,
            onKeyDown: handleConfirmKeyDown,
            datapointMessages,
          }
        : {
            disabled: confirmButtonDisabled,
            focused: confirmButtonFocused,
            label: confirmButtonLabel,
            startIcon: confirmButtonStartIcon,
            onClick: handleConfirmClick,
            onKeyDown: handleConfirmKeyDown,
            datapointMessages,
          },
    [
      showValidateButton,
      intl,
      handleValidateClick,
      datapointMessages,
      confirmButtonDisabled,
      confirmButtonFocused,
      confirmButtonLabel,
      confirmButtonStartIcon,
      handleConfirmClick,
      handleConfirmKeyDown,
    ]
  );

  return result;
};
